import { useEffect, useMemo, useState } from 'react';
import { endOfMonth, startOfMonth } from 'date-fns';

import {
    Alert, Box, FormControl, InputLabel, MenuItem,
    Select, Stack, ToggleButton, ToggleButtonGroup
} from '@mui/material';

import { AVERAGE_TICKET, months } from '../../../../constants/constants';
import { buildDailyAverageTicketMetrics } from '../../../../helpers/admin/dashboard/dashboardHelper';
import DashboardCard from '../components/DashboardCard';
import DailyAverageTicketCalendar from './DailyAverageTicketCalendar';
import TicketPromedio from './TicketPromedio';

const DailyAverageTicket = ({ year, orders, customers, today }) => {
    const selectedYear = Number(year) || today.getFullYear();
    const defaultMonth = selectedYear === today.getFullYear() ? today.getMonth() : 0;
    const [segment, setSegment] = useState('general');
    const [month, setMonth] = useState(defaultMonth);
    const [day, setDay] = useState(
        selectedYear === today.getFullYear() ? today.getDate() : 1
    );
    const metrics = useMemo(
        () => buildDailyAverageTicketMetrics(
            orders, customers, selectedYear, month, segment
        ),
        [customers, month, orders, segment, selectedYear]
    );
    const selectedDay = metrics.rows[day - 1] ?? metrics.rows[0];

    useEffect(() => {
        const nextMonth = selectedYear === today.getFullYear() ? today.getMonth() : 0;
        const nextDay = selectedYear === today.getFullYear() ? today.getDate() : 1;
        setMonth(nextMonth);
        setDay(nextDay);
    }, [selectedYear, today]);

    useEffect(() => {
        setDay(currentDay => Math.min(currentDay, metrics.rows.length));
    }, [metrics.rows.length]);

    const chartData = {
        ...AVERAGE_TICKET.generalTktGraph,
        labels: metrics.labels,
        datasets: [{
            ...AVERAGE_TICKET.generalTktGraph.datasets[0],
            label: segment === 'general'
                ? 'Ticket promedio general'
                : 'Ticket promedio de primera compra',
            data: metrics.values
        }]
    };
    const calendarOperationLabel = segment === 'general'
        ? { singular: 'pedido', plural: 'pedidos' }
        : { singular: 'primera compra', plural: 'primeras compras' };
    const selectedOperationLabel = selectedDay?.count === 1
        ? calendarOperationLabel.singular
        : calendarOperationLabel.plural;

    return (
        <DashboardCard
            title={`Ticket promedio por día (${selectedYear})`}
            contentSx={{ pt: 2 }}
        >
            <Stack spacing={2.5}>
                <ToggleButtonGroup
                    value={segment}
                    exclusive
                    size="small"
                    onChange={(_, value) => value && setSegment(value)}
                    aria-label="Seleccionar tipo de ticket promedio"
                >
                    <ToggleButton value="general">General</ToggleButton>
                    <ToggleButton value="newCustomers">Clientes nuevos</ToggleButton>
                </ToggleButtonGroup>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="daily-ticket-month-label">Mes</InputLabel>
                        <Select
                            labelId="daily-ticket-month-label"
                            value={month}
                            label="Mes"
                            onChange={({ target }) => setMonth(Number(target.value))}
                        >
                            {months.map((monthName, index) => (
                                <MenuItem key={monthName} value={index}>{monthName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="daily-ticket-day-label">Día</InputLabel>
                        <Select
                            labelId="daily-ticket-day-label"
                            value={day}
                            label="Día"
                            onChange={({ target }) => setDay(Number(target.value))}
                        >
                            {metrics.rows.map(row => (
                                <MenuItem key={row.day} value={row.day}>{row.day}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Alert severity={selectedDay?.count > 0 ? 'info' : 'warning'}>
                    {selectedDay?.date}: {selectedDay?.formattedAverage} de ticket promedio
                    {' '}en {selectedDay?.count ?? 0} {selectedOperationLabel}.
                </Alert>

                <Box sx={{ width: '100%' }}>
                    <TicketPromedio
                        title="Detalle diario de ticket promedio"
                        data={chartData}
                        dataTable={metrics.rows}
                        showHeading={false}
                        showTable={false}
                    />
                </Box>

                <DailyAverageTicketCalendar
                    rows={metrics.rows}
                    year={selectedYear}
                    month={month}
                    selectedDay={day}
                    onSelectDay={setDay}
                    operationLabel={calendarOperationLabel}
                    segment={segment}
                    range={{
                        startDate: startOfMonth(new Date(selectedYear, month, 1)),
                        endDate: endOfMonth(new Date(selectedYear, month, 1))
                    }}
                    exportFilename={`pedidos_ticket-promedio_${segment}_${selectedYear}-${String(month + 1).padStart(2, '0')}.csv`}
                />
            </Stack>
        </DashboardCard>
    );
};

export default DailyAverageTicket;
